import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FileText, Loader2, Printer, Search, AlertTriangle } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { asList } from '../../lib/chart-helpers.js'
import { usePersistedState } from '../../lib/persist.js'
import { Card, DateRange, fmt, toISO, parseISO, todayISO, INPUT } from '../statistic/statShared.jsx'
import { buildRows, groupByBranch, procedureNames, thDay, fmtPhone } from '../../lib/followUpDoc.js'
import { FOLLOWUP_STORAGE_KEY } from './FollowUpPrint.jsx'

/**
 * /reports/reimbursement?tab=followup — ออกเอกสารติดตาม.
 *
 * ONE JOB: turn a payer's ค้างรับ claim lines into an A4 letter that can be sent to that
 * payer. It never writes anything — no status change, no Xano call that is not a read.
 *
 * Flow: pick ช่วงวันที่รักษา + ผู้จ่าย (+ สาขา) → โหลดรายการ → untick anything that should
 * not go out → สร้างเอกสาร opens /reports/reimbursement/followup-print in a new tab, where
 * the browser's Print → Save as PDF produces the file. Same hand-off as SsoImportPrint:
 * the built document travels through localStorage, because the procedures cost one
 * request per visit and are not worth fetching twice.
 *
 * WHAT IS FIXED AND WHAT IS NOT (Ram, 2026-10-14). เรียน, ผู้ติดตาม, โทร, อีเมล and the body
 * text are NOT editable — every letter that leaves the clinic says the same thing in the
 * same voice, and the follow-up person is whoever pressed export, read from their own
 * employee record. Only หมายเหตุ is free text. The letterhead is always บริษัท สไมล์ซีซันส์
 * จำกัด, one letter across branches, grouped by เดือน/ปี of the visit.
 *
 * ⚠️ รายการที่เบิก is NOT the claim line's own name. That line is the reversal
 * ("เบิกคืนบริษัทกรุงไทย AXA") and says nothing to a payer; the procedures are the
 * treatment's priced lines, fetched per visit (followUpDoc.js#procedureNames).
 */

const AGE_OPTIONS = [
  { v: 0, label: 'ทั้งหมด' },
  { v: 30, label: 'ค้างเกิน 30 วัน' },
  { v: 60, label: 'ค้างเกิน 60 วัน' },
  { v: 90, label: 'ค้างเกิน 90 วัน' },
]
const MAX_PAGES = 6
const CONCURRENCY = 4

const BODY = 'คลินิกขอนำส่งรายการค่ารักษาพยาบาลที่ได้ส่งเรียกเก็บแล้วแต่ยังไม่ได้รับชำระ ตามรายละเอียดด้านล่าง ' +
  'จึงเรียนมาเพื่อโปรดตรวจสอบสถานะของแต่ละรายการ และแจ้งกำหนดการชำระ หรือแจ้งเหตุผลในกรณีที่รายการใดไม่สามารถชำระได้ ' +
  'หากต้องการเอกสารประกอบเพิ่มเติมสำหรับรายการใด สามารถแจ้งกลับมาได้ทันที'
// Given by Ram, 2026-10-14 — the clinic's insurance desk line and mailbox.
const CLINIC_PHONE = '02-114-3274'
const CLINIC_EMAIL = 'insurance@smileseasons.com'
// The letterhead company (Xano `company.no` 1). Its address and tax id are read from the
// branch list, never typed here.
const LETTERHEAD_COMPANY = 'บริษัท สไมล์ซีซันส์ จำกัด'

/** Run `fn` over `items` with at most `n` in flight. Never rejects — failures come back as null. */
async function pool(items, n, fn) {
  const out = new Array(items.length)
  let i = 0
  async function worker() {
    while (i < items.length) {
      const k = i++
      try { out[k] = await fn(items[k]) } catch { out[k] = null }
    }
  }
  await Promise.all(Array.from({ length: Math.min(n, items.length) }, worker))
  return out
}

export default function FollowUpLetter() {
  // Bangkok day, not the browser's — it is also the letter's issue date and the
  // reference for ค้าง (วัน)
  const today = useMemo(() => todayISO(), [])
  const monthStart = useMemo(() => { const d = parseISO(today); return toISO(new Date(d.getFullYear(), d.getMonth() - 3, 1)) }, [today])
  const [start, setStart] = usePersistedState('fu:start', monthStart)
  const [end, setEnd] = usePersistedState('fu:end', today)
  const [payer, setPayer] = usePersistedState('fu:payer', '')
  const [branchId, setBranchId] = usePersistedState('fu:branch', '0')
  const [minAge, setMinAge] = usePersistedState('fu:minAge', 0)

  const [remark, setRemark] = useState('')
  /* ผู้ติดตาม = the exporting user's OWN employee record — Thai ชื่อ นามสกุล (`name` holds
     both) and their phone. null = loading, false = this login has no employee record. */
  const [me, setMe] = useState(null)
  useEffect(() => {
    cachedGet('/api/me/profile')
      .then((r) => setMe({ name: String(r?.name || '').trim(), phone: fmtPhone(r?.phone) }))
      .catch(() => setMe(false))
  }, [])
  const to = payer ? `เจ้าหน้าที่ผู้เกี่ยวข้อง ${payer}` : ''
  const phones = [me?.phone, CLINIC_PHONE].filter(Boolean).join(' , ')

  const [branches, setBranches] = useState([])
  const [company, setCompany] = useState(null)
  const [doctors, setDoctors] = useState(null)
  useEffect(() => {
    cachedGet('/api/branches').then((r) => {
      const list = asList(r)
      setBranches(list.map((b) => ({ id: String(b.id ?? b.branch_id), name: b.name || `สาขา #${b.id}` })))
      const co = list.map((b) => b.Company || b.company).find((c) => c && String(c.name || '').trim() === LETTERHEAD_COMPANY)
      setCompany({ name: LETTERHEAD_COMPANY, address: String(co?.address || '').trim(), tax: String(co?.taxnumber ?? '').trim() })
    }).catch(() => setCompany({ name: LETTERHEAD_COMPANY, address: '', tax: '' }))
    cachedGet('/api/doctors', { slim: 1 }).then((r) => setDoctors(new Map(asList(r).map((d) => [String(d.id ?? d.doctor_id), d])))).catch(() => setDoctors(new Map()))
  }, [])
  const branchName = useCallback((id) => branches.find((b) => b.id === String(id))?.name || `สาขา #${id}`, [branches])

  /* Payers with something still owed in the window — the picker only offers a letter
     that would have rows on it. Same aggregate the ตรวจสอบ tab paints from (api 639). */
  const [payers, setPayers] = useState({ loading: true, list: [] })
  useEffect(() => {
    let alive = true
    setPayers((p) => ({ ...p, loading: true }))
    apiGet('/api/billing/reimbursement-summary', { start_date: start, end_date: end, branch_id: branchId, doctor_id: '0' })
      .then((r) => {
        if (!alive) return
        const m = new Map()
        for (const t of asList(r?.totals)) {
          const st = String(t.status ?? '').toLowerCase()
          if (st && st !== 'pending') continue
          const k = String(t.label ?? '').trim()
          if (!k) continue
          const cur = m.get(k) || { name: k, lines: 0, amount: 0 }
          cur.lines += Number(t.lines) || 0
          cur.amount += Number(t.advance) || 0
          m.set(k, cur)
        }
        setPayers({ loading: false, list: [...m.values()].filter((p) => p.amount > 0).sort((a, b) => b.amount - a.amount) })
      })
      .catch(() => { if (alive) setPayers({ loading: false, list: [] }) })
    return () => { alive = false }
  }, [start, end, branchId])

  /* The letter's data. Rebuilt on โหลดรายการ only — never silently under the operator's
     ticks, since the rows on screen are the rows that go out. */
  const [data, setData] = useState({ loading: false, err: '', rows: [], missingItems: 0, key: '' })
  const [excluded, setExcluded] = useState(() => new Set())
  const req = useRef(0)

  async function load() {
    if (!payer) return
    const id = ++req.current
    setData((d) => ({ ...d, loading: true, err: '' }))
    try {
      const lines = []
      for (let page = 1; page <= MAX_PAGES; page++) {
        const r = await apiGet('/api/billing/reimbursement-lines', {
          start_date: start, end_date: end, branch_id: branchId, doctor_id: '0', advancepay: payer, per_page: 500, page,
        })
        const items = asList(r)
        lines.push(...items)
        if (page >= (Number(r?.pageTotal) || 1) || items.length < 500) break
      }
      if (id !== req.current) return
      const owed = buildRows(lines, { today })
      // what was done on each visit
      const visits = [...new Map(lines.map((b) => [String(b.treatment_id), b])).values()]
        .filter((b) => owed.some((r) => r.treatmentId === String(b.treatment_id)))
      const procs = await pool(visits, CONCURRENCY, (b) =>
        apiGet('/api/billing', { treatment_id: b.treatment_id, patient_id: b.patient_id, per_page: 200 }).then((r) => procedureNames(asList(r))))
      const procedures = new Map(visits.map((b, i) => [String(b.treatment_id), procs[i] || []]))
      if (id !== req.current) return
      const rows = buildRows(lines, { doctors: doctors || new Map(), procedures, today })
      setExcluded(new Set())
      setData({
        loading: false, err: '', rows,
        missingItems: rows.filter((r) => !r.items).length,
        key: `${payer}|${start}|${end}|${branchId}`,
      })
    } catch (e) {
      if (id === req.current) setData((d) => ({ ...d, loading: false, err: String(e?.message || e) }))
    }
  }

  const scopeKey = `${payer}|${start}|${end}|${branchId}`
  const stale = data.key && data.key !== scopeKey
  const visible = useMemo(() => data.rows.filter((r) => !minAge || (r.age ?? 0) > minAge), [data.rows, minAge])
  const chosen = visible.filter((r) => !excluded.has(r.id))
  const groups = useMemo(() => groupByBranch(visible, branches.map((b) => b.id)), [visible, branches])
  const total = chosen.reduce((s, r) => s + r.amount, 0)

  const toggle = (rid) => setExcluded((s) => { const n = new Set(s); n.has(rid) ? n.delete(rid) : n.add(rid); return n })
  const toggleAll = (on) => setExcluded(on ? new Set() : new Set(visible.map((r) => r.id)))

  function openPrint() {
    const doc = {
      payer, to, body: BODY, remark: remark.trim(),
      contact: me?.name || '', phones, email: CLINIC_EMAIL, company,
      start, end, issued: today, minAge,
      rows: chosen.map((r) => ({ ...r, branchName: branchName(r.branchId) })),
      count: chosen.length, total,
    }
    try {
      localStorage.setItem(FOLLOWUP_STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), doc }))
    } catch {
      setData((d) => ({ ...d, err: 'บันทึกข้อมูลเอกสารไม่สำเร็จ (พื้นที่เบราว์เซอร์เต็มหรือถูกปิด)' }))
      return
    }
    window.open('/reports/reimbursement/followup-print', '_blank', 'noopener')
  }

  return (
    <div className="space-y-5">
      <Card icon={<Search size={18} />} title="เลือกรายการที่จะติดตาม" subtitle="ช่วงวันที่รักษา · ผู้จ่าย · สาขา — เฉพาะรายการสถานะ ค้างรับ">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[12px] text-slate-500 mb-1">ช่วงวันที่รักษา</div>
            <DateRange start={start} end={end} today={today} onStart={setStart} onEnd={setEnd} />
          </div>
          <label className="block">
            <div className="text-[12px] text-slate-500 mb-1">ผู้จ่าย</div>
            <select value={payer} onChange={(e) => setPayer(e.target.value)} className={`${INPUT} min-w-[240px]`}>
              <option value="">{payers.loading ? 'กำลังโหลด…' : '— เลือกผู้จ่าย —'}</option>
              {payers.list.map((p) => <option key={p.name} value={p.name}>{p.name} · ค้างรับ ฿{fmt(p.amount)}</option>)}
              {payer && !payers.list.some((p) => p.name === payer) ? <option value={payer}>{payer}</option> : null}
            </select>
          </label>
          <label className="block">
            <div className="text-[12px] text-slate-500 mb-1">สาขา</div>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={`${INPUT} min-w-[160px]`}>
              <option value="0">ทุกสาขา</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
          <button type="button" onClick={load} disabled={!payer || data.loading || doctors === null}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white px-3.5 py-2 text-[13px] font-medium">
            {data.loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} โหลดรายการ
          </button>
        </div>
        {stale ? (
          <div className="mt-3 text-[12.5px] text-amber-700">ตัวกรองเปลี่ยนแล้ว — กด โหลดรายการ อีกครั้งเพื่ออัปเดตรายการด้านล่าง</div>
        ) : null}
        {data.err ? <div className="mt-3 text-[12.5px] text-rose-700">{data.err}</div> : null}
      </Card>

      {data.key ? (
        <Card icon={<FileText size={18} />} title="รายการค้างรับ" badge={`${fmt(chosen.length)} / ${fmt(visible.length)} รายการ`}
          subtitle="ติ๊กออกรายการที่ไม่ต้องการส่ง · หน้านี้แยกตามสาขาเพื่อเลือกง่าย — ในเอกสาร PDF จะเรียงตามเดือน/ปีที่รักษา"
          right={(
            <div className="flex items-center gap-2">
              <select value={minAge} onChange={(e) => setMinAge(Number(e.target.value))} className={INPUT}>
                {AGE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              <div className="text-right">
                <div className="text-[11.5px] text-slate-400">ยอดที่จะส่งติดตาม</div>
                <div className="text-[17px] font-semibold text-orange-600 tabular-nums">฿{fmt(total)}</div>
              </div>
            </div>
          )}>
          {data.missingItems ? (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              {fmt(data.missingItems)} รายการดึงรายการรักษาไม่ได้ — ช่อง รายการที่เบิก จะว่าง ลองกด โหลดรายการ อีกครั้ง
            </div>
          ) : null}

          {visible.length ? (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-50 text-slate-500 text-[12px]">
                  <tr>
                    <th className="px-3 py-2 w-8"><input type="checkbox" checked={chosen.length === visible.length} onChange={(e) => toggleAll(e.target.checked)} /></th>
                    <th className="px-2 py-2 text-left font-medium">วันที่รักษา</th>
                    <th className="px-2 py-2 text-left font-medium">ชื่อ-นามสกุล</th>
                    <th className="px-2 py-2 text-left font-medium">HN</th>
                    <th className="px-2 py-2 text-left font-medium">ทันตแพทย์</th>
                    <th className="px-2 py-2 text-left font-medium">รายการที่เบิก</th>
                    <th className="px-2 py-2 text-left font-medium">เลขที่เคลม</th>
                    <th className="px-2 py-2 text-right font-medium">ค้าง (วัน)</th>
                    <th className="px-3 py-2 text-right font-medium">ยอดเบิก</th>
                  </tr>
                </thead>
                {groups.map((g) => (
                  <tbody key={g.branchId}>
                    <tr className="bg-slate-50/70 border-t border-slate-200">
                      <td colSpan={8} className="px-3 py-1.5 text-[12.5px] font-semibold text-slate-700">{branchName(g.branchId)} · {fmt(g.rows.length)} รายการ</td>
                      <td className="px-3 py-1.5 text-right text-[12.5px] font-semibold text-slate-700 tabular-nums">฿{fmt(g.total)}</td>
                    </tr>
                    {g.rows.map((r) => {
                      const off = excluded.has(r.id)
                      return (
                        <tr key={r.id} className={`border-t border-slate-100 ${off ? 'opacity-40' : ''}`}>
                          <td className="px-3 py-2"><input type="checkbox" checked={!off} onChange={() => toggle(r.id)} /></td>
                          <td className="px-2 py-2 whitespace-nowrap text-slate-600">{thDay(r.day)}</td>
                          <td className="px-2 py-2 text-slate-800">{r.patient || '—'}</td>
                          <td className="px-2 py-2 font-mono text-[12px] text-slate-500 whitespace-nowrap">{r.hn || '—'}</td>
                          <td className="px-2 py-2 text-slate-600">{r.doctor || '—'}</td>
                          <td className="px-2 py-2 text-slate-600">{r.items || <span className="text-amber-600">—</span>}</td>
                          <td className="px-2 py-2 font-mono text-[12px] text-slate-600 whitespace-nowrap">{r.claimRef || '—'}</td>
                          <td className={`px-2 py-2 text-right tabular-nums ${r.age > 90 ? 'text-rose-600 font-medium' : r.age > 60 ? 'text-orange-600' : 'text-slate-500'}`}>{r.age ?? '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-800">{fmt(r.amount)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                ))}
              </table>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-[13px] text-slate-400">
              ไม่มีรายการค้างรับ{minAge ? `ที่ค้างเกิน ${minAge} วัน` : ''}ในช่วงนี้
            </div>
          )}
        </Card>
      ) : null}

      {data.key && visible.length ? (
        <Card icon={<Printer size={18} />} title="ข้อความในเอกสาร" subtitle="ข้อความมาตรฐานของคลินิก — แก้ได้เฉพาะ หมายเหตุ">
          <dl className="grid gap-x-6 gap-y-2 text-[13px] md:grid-cols-[110px_1fr]">
            <dt className="text-slate-500">หัวกระดาษ</dt>
            <dd className="text-slate-800">{company?.name || '…'}{company && !company.tax ? <span className="ml-2 text-amber-600 text-[12px]">ไม่พบเลขผู้เสียภาษีในข้อมูลสาขา</span> : null}</dd>
            <dt className="text-slate-500">เรียน</dt>
            <dd className="text-slate-800">{to}</dd>
            <dt className="text-slate-500">ผู้ติดตาม</dt>
            <dd className="text-slate-800">
              {me === null ? 'กำลังโหลด…' : me && me.name ? me.name
                : <span className="text-amber-700">บัญชีนี้ไม่มีข้อมูลพนักงาน — เอกสารจะไม่มีชื่อผู้ติดตาม</span>}
            </dd>
            <dt className="text-slate-500">เบอร์โทร</dt>
            <dd className="text-slate-800 tabular-nums">{phones}</dd>
            <dt className="text-slate-500">อีเมล</dt>
            <dd className="text-slate-800">{CLINIC_EMAIL}</dd>
            <dt className="text-slate-500">ข้อความ</dt>
            <dd className="text-slate-700 leading-relaxed">{BODY}</dd>
            <dt className="text-slate-500 pt-1.5">หมายเหตุ</dt>
            <dd>
              <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} className={`${INPUT} w-full`}
                placeholder="(ไม่บังคับ) เช่น รายการลำดับที่ 3 ได้ส่งเอกสารเพิ่มเติมแล้วเมื่อวันที่ …" />
            </dd>
          </dl>
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-[12.5px] text-slate-500">
              {fmt(chosen.length)} รายการ · ฿{fmt(total)} · ในเอกสารจัดกลุ่มตามเดือน/ปีที่รักษา
            </div>
            <button type="button" onClick={openPrint} disabled={!chosen.length || stale || me === null || !company}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white px-4 py-2 text-[13px] font-medium">
              <FileText size={15} /> สร้างเอกสาร PDF (A4)
            </button>
          </div>
        </Card>
      ) : null}
    </div>
  )
}
